import { useEffect, useMemo } from 'react';

import TrackingClient, { EventId, EventWhitelist } from '@pegaxy/event-tracking-webworker';
import { useLocation } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { useAuthStore } from '../../stores/auth';
import { sendButtonClickTrackingEvent, sendErrorTrackingEvent, sendScreenViewTrackingEvent, sendHeartBeatTrackingEvent } from './trackingEvents';

declare global {
  interface Crypto {
    randomUUID: () => string;
  }
}

export default function useTrackingData() {
  const trackingClient = useMemo(() => new TrackingClient(), []);
  const location = useLocation();
  const { user } = useAuthStore(({ user }) => ({ user }), shallow);

  const trackingUser = user ? { address: user.publicAddress } : undefined;

  useEffect(() => {
    [EventId.ClickButton, EventId.ScreenView, EventId.Error].forEach((eventId) => {
      EventWhitelist.INSTANCE.add(eventId);
    });

    // const worker_url = new URL(
    //   './worker.js',
    //   import.meta.url,
    // );
    // const workerUrl = 'https://play.pegaxy.io/js/worker.js';
    // const workerBlob = new Blob([
    //     'importScripts(' + JSON.stringify(workerUrl) + ')',
    // ], {type: 'application/javascript'});
    // const blobUrl = window.URL.createObjectURL(workerBlob);

    trackingClient.putWorker(new Worker(new URL(
      '../../../node_modules/@pegaxy/event-tracking-webworker/dist/worker/worker.js',
      import.meta.url
    )));
    trackingClient.putConfig({
      Env: process.env.NODE_ENV === 'test' ? 'development' : process.env.NODE_ENV,
    });
    trackingClient.setCommitInterval(30);
    trackingClient.setChecksumSalt(crypto.randomUUID());
  }, [trackingClient]);

  useEffect(() => {
    if (user) {
      trackingClient.watchHeartBeat(window.location.href, {
        address: user.publicAddress,
      });
    }
  }, [location, user, trackingClient]);

  useEffect(() => {
    if (location) {
      sendScreenViewTrackingEvent(trackingClient, trackingUser)(
        'SCREEN_VIEW',
        Date.now(),
        Date.now(),
        location.pathname,
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (user) {
      trackingClient.defineMetadata({
        address: user.publicAddress,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const heartBeatTimer = setTimeout(() => {
      if (location) {
        sendHeartBeatTrackingEvent(trackingClient, trackingUser);
      }
    }, 30000);
    return () => clearTimeout(heartBeatTimer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sendButtonClickTrackingEvent: sendButtonClickTrackingEvent(trackingClient, trackingUser),
    sendScreenViewTrackingEvent: sendScreenViewTrackingEvent(trackingClient, trackingUser),
    sendHeartBeatTrackingEvent: sendHeartBeatTrackingEvent(trackingClient, trackingUser),
    sendErrorTrackingEvent: sendErrorTrackingEvent(trackingClient, trackingUser),
  };
}
